/**
 * A simple pub/sub service that allows the Slides iframe to register
 * a "start present" handler, and the Confluence header Present button
 * to invoke it.
 */
export class ContentPresentationService {
	private handler: (() => void) | null = null;
	private availabilityListeners: Set<(isAvailable: boolean) => void> = new Set();
	private presentSlidesEnabled = true;
	private presentSlidesEnabledListeners: Set<(enabled: boolean) => void> = new Set();

	/**
	 * Register a handler that starts presentation mode.
	 * Called by the content iframe in the Confluence header
	 *
	 * @returns An unsubscribe function to remove the handler.
	 */
	onStartPresent(cb: () => void): () => void {
		this.handler = cb;
		this.presentSlidesEnabled = true;
		this.notifyAvailabilityListeners(true);
		this.notifyPresentSlidesEnabledListeners();
		return () => {
			if (this.handler === cb) {
				this.handler = null;
				this.presentSlidesEnabled = true;
				this.notifyAvailabilityListeners(false);
				this.notifyPresentSlidesEnabledListeners();
			}
		};
	}

	/**
	 * Lets the slides iframe enable or disable the shell Present control while the
	 * handler stays registered (for example while a prompt slide is active).
	 */
	setPresentSlidesEnabled(enabled: boolean): void {
		if (this.presentSlidesEnabled === enabled) {
			return;
		}
		this.presentSlidesEnabled = enabled;
		this.notifyPresentSlidesEnabledListeners();
	}

	/**
	 * Whether the header Present control should accept interaction. Only meaningful
	 * when {@link isAvailable} is true.
	 */
	get isPresentSlidesEnabled(): boolean {
		return this.presentSlidesEnabled;
	}

	/**
	 * Trigger presentation mode. Called by the Confluence header Present button.
	 * Invokes the handler registered by the content iframe.
	 */
	startPresent(): void {
		if (!this.presentSlidesEnabled) {
			return;
		}
		this.handler?.();
	}

	/**
	 * Returns whether a presentation handler is currently registered.
	 */
	get isAvailable(): boolean {
		return this.handler !== null;
	}

	/**
	 * Subscribe to availability changes. Called by UI components
	 * to reactively show/hide the Present button.
	 *
	 * @returns An unsubscribe function.
	 */
	onAvailabilityChange(cb: (isAvailable: boolean) => void): () => void {
		this.availabilityListeners.add(cb);
		return () => {
			this.availabilityListeners.delete(cb);
		};
	}

	/**
	 * Subscribe to changes for whether the Present button should be interactive.
	 *
	 * @returns An unsubscribe function.
	 */
	onPresentSlidesEnabledChange(cb: (enabled: boolean) => void): () => void {
		this.presentSlidesEnabledListeners.add(cb);
		return () => {
			this.presentSlidesEnabledListeners.delete(cb);
		};
	}

	private notifyAvailabilityListeners(isAvailable: boolean): void {
		this.availabilityListeners.forEach((listener) => listener(isAvailable));
	}

	private notifyPresentSlidesEnabledListeners(): void {
		this.presentSlidesEnabledListeners.forEach((listener) => listener(this.presentSlidesEnabled));
	}

	dispose(): void {
		this.handler = null;
		this.presentSlidesEnabled = true;
		this.availabilityListeners.clear();
		this.presentSlidesEnabledListeners.clear();
	}
}

export const contentPresentationService: ContentPresentationService =
	new ContentPresentationService();
